import { inject, Injectable } from '@angular/core';
import { CONFIG_TOKEN, Plan, PlanTypes, ReferenceDataForPlans } from '@pxl/models/common';
import { AnimalFeedingService, LoginService } from '@pxl/models/contracts';
import { FeedingRecommendationModel, FeedingRecommendationService, FeedingRecommendationType } from '@pxl/shared/generated';
import { from, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { DefaultAssignmentPlanService } from './plans/assignment-plan.service';
import { LanguageService, RESOURCES } from '@pxl/shared/translation';

@Injectable()
export class DefaultAnimalFeedingService extends DefaultAssignmentPlanService implements AnimalFeedingService {
    protected readonly feedingRecommendationService = inject(FeedingRecommendationService);
    protected readonly loginService = inject(LoginService);
    protected readonly languageService = inject(LanguageService);
    protected readonly config = inject(CONFIG_TOKEN);

    private readonly resourceKeys = {
        ...RESOURCES.petsxl.ui.dashboard.animal_feeding.details,
        types: RESOURCES.petsxl.ui.dashboard.animal_feeding.recommendation.types,
        assignment: RESOURCES.petsxl.ui.dashboard.animal_feeding.animal_feeding_recommendation_assignment,
        deleteDialog: RESOURCES.petsxl.ui.dashboard.animal_feeding.delete_confirm_dialog,
    };

    public getTranslatedFeedingRecommendationType(type: FeedingRecommendationType): string {
        switch (type) {
            case FeedingRecommendationType.AnimalFeed:
                return this.languageService.translate(this.resourceKeys.types.animal_feed);
            case FeedingRecommendationType.Recipe:
                return this.languageService.translate(this.resourceKeys.types.recipe);
            case FeedingRecommendationType.SubmittedFood:
                return this.languageService.translate(this.resourceKeys.types.submitted_food);
            default:
                return this.languageService.translate(this.resourceKeys.types.animal_feed);
        }
    }

    public getFeedingRecommendations(): Observable<FeedingRecommendationModel[]> {
        return this.feedingRecommendationService.getFeedingRecommendations(this.config.API_VERSION, this.loginService.getToken());
    }
    public getFeedingRecommendationsByPatientId = (patientId: string): Observable<FeedingRecommendationModel[]> => {
        if (!patientId) return of([]);
        return this.getFeedingRecommendations().pipe(
            map((recommendations) => recommendations.filter((recommendation) => recommendation.patientId === patientId)),
        );
    };

    public getFeedingRecommendationById(id: string): Observable<FeedingRecommendationModel> {
        return this.feedingRecommendationService.getFeedingRecommendationById(id, this.config.API_VERSION, this.loginService.getToken());
    }

    public getAnimalFeedingPlansByPatientId(
        patientId: string,
        includeArchived?: boolean,
        includeHidden?: boolean,
        prefetchedReferences?: Partial<ReferenceDataForPlans>,
    ): Observable<Plan[]> {
        return from(
            this.getPlans([PlanTypes.AnimalFeedingPlan], [patientId], includeArchived, includeHidden, {
                patients: prefetchedReferences?.patients ?? [],
                veterinarians: prefetchedReferences?.veterinarians ?? [],
                plans: prefetchedReferences?.plans ?? [],
                appointments: prefetchedReferences?.appointments ?? [],
                medicationRoutesOfAdministration: prefetchedReferences?.medicationRoutesOfAdministration ?? [],
                medications: prefetchedReferences?.medications ?? [],
                medicationUnits: prefetchedReferences?.medicationUnits ?? [],
                assignmentTypes: prefetchedReferences?.assignmentTypes ?? [],
            }),
        );
    }
}
